import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import TagIcon from "@mui/icons-material/Tag";
import StyleIcon from "@mui/icons-material/Style";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0"];

const resolveApiOrigin = () => {
  const override = process.env.REACT_APP_API_BASE;
  if (override) {
    return override.replace(/\/$/, "");
  }
  if (typeof window === "undefined") {
    return "https://server-9p4hx.kinsta.app";
  }
  const hostname = (window.location.hostname || "").toLowerCase();
  if (
    LOCAL_HOSTNAMES.includes(hostname) ||
    hostname.endsWith(".local") ||
    hostname.startsWith("192.168.")
  ) {
    return "https://server-9p4hx.kinsta.app";
  }
  if (
    hostname === "bo.luxuraelite.ro" ||
    hostname.endsWith(".luxuraelite.ro")
  ) {
    return "https://server-9p4hx.kinsta.app";
  }
  return "https://server-9p4hx.kinsta.app";
};

const API_BASE = `${resolveApiOrigin()}/api/seowoo`;
const emptyTermForm = { name: "", slug: "", description: "" };

const resolveTermId = (term) =>
  term?.id ?? term?.term_id ?? term?.termId ?? term?.slug;

const SEOAttributesPage = () => {
  const [attributes, setAttributes] = useState([]);
  const [attributesLoading, setAttributesLoading] = useState(true);
  const [attributesError, setAttributesError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [termsState, setTermsState] = useState({});
  const [termForms, setTermForms] = useState({});
  const [termFilters, setTermFilters] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editingTerm, setEditingTerm] = useState(null);
  const [wooTags, setWooTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsError, setTagsError] = useState(null);
  const [tagFilter, setTagFilter] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchAttributes = async () => {
    setAttributesLoading(true);
    setAttributesError(null);
    try {
      const res = await fetch(`${API_BASE}/attributes`);
      if (!res.ok) {
        throw new Error("Nu am reușit să preluăm atributele.");
      }
      const text = await res.text();
      let payload;
      try {
        payload = text ? JSON.parse(text) : { ok: false };
      } catch {
        throw new Error(
          "Răspunsul API nu este JSON valid. Verifică plugin-ul WooCommerce."
        );
      }
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const normalized =
        payload.data?.attributes ??
        (Array.isArray(payload.data) ? payload.data : []);
      setAttributes(Array.isArray(normalized) ? normalized : []);
    } catch (err) {
      setAttributesError(err.message || "A apărut o eroare.");
    } finally {
      setAttributesLoading(false);
    }
  };

  const fetchTerms = async (attributeId) => {
    setTermsState((prev) => ({
      ...prev,
      [attributeId]: {
        ...(prev[attributeId] || {}),
        loading: true,
        error: null,
      },
    }));
    try {
      const res = await fetch(`${API_BASE}/attributes/${attributeId}/terms`);
      if (!res.ok) {
        throw new Error("Nu am reușit să preluăm termenii.");
      }
      const text = await res.text();
      let payload;
      try {
        payload = text ? JSON.parse(text) : { ok: false };
      } catch {
        throw new Error(
          "Răspunsul API nu este JSON valid. Verifică plugin-ul WooCommerce."
        );
      }
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const normalizedTerms =
        payload.data?.terms ??
        (Array.isArray(payload.data) ? payload.data : []);
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: {
          ...(prev[attributeId] || {}),
          loading: false,
          items: Array.isArray(normalizedTerms) ? normalizedTerms : [],
        },
      }));
    } catch (err) {
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: {
          ...(prev[attributeId] || {}),
          loading: false,
          error: err.message || "A apărut o eroare.",
        },
      }));
    }
  };

  const handleAccordionChange = (attributeId) => (_, isExpanded) => {
    setExpandedId(isExpanded ? attributeId : null);
    if (isExpanded && !termsState[attributeId]?.items) {
      fetchTerms(attributeId);
    }
  };

  const handleFormChange = (attributeId, field, value) => {
    setTermForms((prev) => ({
      ...prev,
      [attributeId]: {
        ...(prev[attributeId] || emptyTermForm),
        [field]: value,
      },
    }));
  };

  const resetForm = (attributeId) => {
    setTermForms((prev) => ({
      ...prev,
      [attributeId]: { ...emptyTermForm },
    }));
  };

  const handleTermFilterChange = (attributeId, value) => {
    setTermFilters((prev) => ({
      ...prev,
      [attributeId]: value,
    }));
  };

  const handleStartEditTerm = (attributeId, term) => {
    const termId = resolveTermId(term);
    setEditingTerm({
      attributeId,
      termId,
      value: term.name || "",
      saving: false,
    });
  };

  const handleCancelEditTerm = () => {
    setEditingTerm(null);
  };

  const handleSaveEditTerm = async () => {
    if (!editingTerm || !editingTerm.value.trim()) {
      showSnackbar("Introduce denumirea termenului.", "warning");
      return;
    }
    const { attributeId, termId } = editingTerm;
    setEditingTerm((prev) => ({ ...prev, saving: true }));
    try {
      const res = await fetch(
        `${API_BASE}/attributes/${attributeId}/terms/${termId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: editingTerm.value.trim() }),
        }
      );
      if (!res.ok) {
        throw new Error("Nu am putut actualiza termenul.");
      }
      const text = await res.text();
      let payload;
      try {
        payload = text ? JSON.parse(text) : { ok: false };
      } catch {
        throw new Error(
          "Răspunsul API nu este JSON valid. Verifică plugin-ul WooCommerce."
        );
      }
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const updatedTerm =
        payload.data?.term ?? payload.data?.terms?.[0] ?? payload.data;
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: {
          ...(prev[attributeId] || {}),
          items: (prev[attributeId]?.items || []).map((term) =>
            resolveTermId(term) === termId
              ? { ...term, ...updatedTerm, name: editingTerm.value.trim() }
              : term
          ),
        },
      }));
      showSnackbar("Termen actualizat.");
      setEditingTerm(null);
    } catch (err) {
      setEditingTerm((prev) => ({ ...(prev || {}), saving: false }));
      showSnackbar(err.message || "Nu am putut actualiza termenul.", "error");
    }
  };

  const handleAddTerm = async (attributeId) => {
    const formValues = termForms[attributeId] || emptyTermForm;
    if (!formValues.name.trim()) {
      showSnackbar("Completează numele termenului.", "warning");
      return;
    }

    setTermsState((prev) => ({
      ...prev,
      [attributeId]: { ...(prev[attributeId] || {}), saving: true },
    }));

    try {
      const res = await fetch(`${API_BASE}/attributes/${attributeId}/terms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formValues.name.trim(),
          slug: formValues.slug.trim() || undefined,
          description: formValues.description.trim() || undefined,
        }),
      });

      if (!res.ok) {
        throw new Error("Nu am putut adăuga termenul.");
      }
      const text = await res.text();
      let payload;
      try {
        payload = text ? JSON.parse(text) : { ok: false };
      } catch {
        throw new Error(
          "Răspunsul API nu este JSON valid. Verifică plugin-ul WooCommerce."
        );
      }
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const newTerm =
        payload.data?.term ?? payload.data?.terms?.[0] ?? payload.data;
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: {
          ...(prev[attributeId] || {}),
          saving: false,
          items: [
            newTerm,
            ...(prev[attributeId]?.items &&
            Array.isArray(prev[attributeId].items)
              ? prev[attributeId].items
              : []),
          ].filter(Boolean),
        },
      }));
      resetForm(attributeId);
      showSnackbar("Termen adăugat cu succes.");
    } catch (err) {
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: { ...(prev[attributeId] || {}), saving: false },
      }));
      showSnackbar(err.message || "Nu am putut adăuga termenul.", "error");
    }
  };

  const handleDeleteTerm = async (attributeId, termId) => {
    if (
      !window.confirm(
        "Ești sigur că vrei să ștergi acest termen? Acțiunea este permanentă."
      )
    ) {
      return;
    }
    setTermsState((prev) => ({
      ...prev,
      [attributeId]: { ...(prev[attributeId] || {}), deleting: termId },
    }));
    try {
      const res = await fetch(
        `${API_BASE}/attributes/${attributeId}/terms/${termId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        throw new Error("Nu am putut șterge termenul.");
      }
      const text = await res.text();
      let payload;
      try {
        payload = text ? JSON.parse(text) : { ok: false };
      } catch {
        throw new Error(
          "Răspunsul API nu este JSON valid. Verifică plugin-ul WooCommerce."
        );
      }
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: {
          ...(prev[attributeId] || {}),
          deleting: null,
          items: (prev[attributeId]?.items || []).filter(
            (term) => resolveTermId(term) !== termId
          ),
        },
      }));
      showSnackbar("Termen șters.");
    } catch (err) {
      setTermsState((prev) => ({
        ...prev,
        [attributeId]: { ...(prev[attributeId] || {}), deleting: null },
      }));
      showSnackbar(err.message || "Nu am putut șterge termenul.", "error");
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  const fetchWooTags = async () => {
    setTagsLoading(true);
    setTagsError(null);
    try {
      const res = await fetch(`${API_BASE}/tags`);
      if (!res.ok) {
        throw new Error("Nu am reușit să preluăm tag-urile WooCommerce.");
      }
      const payload = await res.json();
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const tagsData = Array.isArray(payload.data) ? payload.data : [];
      setWooTags(tagsData);
    } catch (err) {
      setTagsError(err.message || "Nu am putut încărca tag-urile.");
    } finally {
      setTagsLoading(false);
    }
  };

  useEffect(() => {
    fetchWooTags();
  }, []);

  const safeAttributes = useMemo(
    () => (Array.isArray(attributes) ? attributes : []),
    [attributes]
  );

  const totalTermsLoaded = useMemo(() => {
    return Object.values(termsState).reduce((sum, state) => {
      if (Array.isArray(state.items)) {
        return sum + state.items.length;
      }
      return sum;
    }, 0);
  }, [termsState]);

  const filteredTags = useMemo(() => {
    if (!tagFilter.trim()) return wooTags;
    const needle = tagFilter.trim().toLowerCase();
    return wooTags.filter((tag) => {
      const source = `${tag.name || ""} ${tag.slug || ""}`.toLowerCase();
      return source.includes(needle);
    });
  }, [wooTags, tagFilter]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            SEO Atribute
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionază rapid atributele și termenii WooCommerce sincronizați
            prin API-ul securizat Luxura.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<StyleIcon />}
            label={`${safeAttributes.length} atribute`}
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<TagIcon />}
            label={`${totalTermsLoaded} termeni încărcați`}
            variant="outlined"
          />
          <Tooltip title="Reîncarcă atributele">
            <IconButton onClick={fetchAttributes} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {attributesLoading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 4,
            background: "rgba(255,255,255,0.8)",
          }}
        >
          <CircularProgress size={48} />
          <Typography mt={2} color="text.secondary">
            Încărcăm atributele…
          </Typography>
        </Paper>
      )}

      {attributesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {attributesError}
        </Alert>
      )}

      {!attributesLoading && !attributesError && attributes.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: "rgba(255,255,255,0.8)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">
            Nu există atribute disponibile momentan.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adaugă atribute în WooCommerce pentru a le gestiona aici.
          </Typography>
        </Paper>
      )}

      {safeAttributes.map((attribute) => {
        const termState = termsState[attribute.id] || {};
        const formValues = termForms[attribute.id] || emptyTermForm;
        const terms = Array.isArray(termState.items) ? termState.items : [];
        const searchValue = termFilters[attribute.id] || "";
        const normalizedSearch = searchValue.trim().toLowerCase();
        const filteredTerms = normalizedSearch
          ? terms.filter((term) => {
              const haystack = `${term.name ?? ""} ${term.slug ?? ""} ${
                term.description ?? ""
              }`.toLowerCase();
              return haystack.includes(normalizedSearch);
            })
          : terms;

        return (
          <Accordion
            key={attribute.id}
            expanded={expandedId === attribute.id}
            onChange={handleAccordionChange(attribute.id)}
            TransitionProps={{ unmountOnExit: true }}
            sx={{
              mb: 2,
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255,255,255,0.95)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 3, py: 2 }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
                width="100%"
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {attribute.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    slug: {attribute.slug} • tip: {attribute.type} • ordine:{" "}
                    {attribute.order_by}
                  </Typography>
                </Box>
                <Chip
                  label={`${terms.length} termeni`}
                  color="secondary"
                  variant="outlined"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 4 }}>
              {termState.loading ? (
                <Box textAlign="center" py={4}>
                  <CircularProgress />
                  <Typography mt={2} color="text.secondary">
                    Încărcăm termenii…
                  </Typography>
                </Box>
              ) : termState.error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {termState.error}
                </Alert>
              ) : (
                <>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ md: "center" }}
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Lista termeni
                    </Typography>
                    <TextField
                      label="Caută termen"
                      size="small"
                      value={searchValue}
                      onChange={(e) =>
                        handleTermFilterChange(attribute.id, e.target.value)
                      }
                      placeholder="ex: verde, lux, etc."
                      sx={{ maxWidth: 260 }}
                    />
                  </Stack>
                  {terms.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Nu există termeni pentru acest atribut. Adaugă unul mai
                      jos.
                    </Alert>
                  ) : filteredTerms.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      Niciun termen nu corespunde căutării „{searchValue}”.
                    </Alert>
                  ) : (
                    <List dense disablePadding>
                      {filteredTerms.map((term) => {
                        const currentTermId = resolveTermId(term);
                        const isEditing =
                          editingTerm &&
                          editingTerm.attributeId === attribute.id &&
                          editingTerm.termId === currentTermId;
                        return (
                          <ListItem
                            key={`${attribute.id}-${currentTermId}`}
                            divider
                            sx={{
                              borderRadius: 2,
                              mb: 1,
                              bgcolor: "rgba(15,23,42,0.03)",
                            }}
                          >
                            <ListItemText
                              primary={
                                isEditing ? (
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <TextField
                                      value={editingTerm.value}
                                      size="small"
                                      onChange={(e) =>
                                        setEditingTerm((prev) =>
                                          prev
                                            ? { ...prev, value: e.target.value }
                                            : prev
                                        )
                                      }
                                      sx={{ minWidth: 200 }}
                                    />
                                    <Chip
                                      label={`${term.count ?? 0} produse`}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  </Stack>
                                ) : (
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography fontWeight={600}>
                                      {term.name}
                                    </Typography>
                                    {term.slug && (
                                      <Chip
                                        label={term.slug}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                    <Chip
                                      label={`${term.count ?? 0} produse`}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  </Stack>
                                )
                              }
                              secondary={
                                term.description ||
                                "Fără descriere specificată."
                              }
                            />
                            <ListItemSecondaryAction>
                              {isEditing ? (
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title="Anulează">
                                    <span>
                                      <IconButton
                                        edge="end"
                                        onClick={handleCancelEditTerm}
                                        disabled={editingTerm?.saving}
                                      >
                                        <CloseIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Salvează">
                                    <span>
                                      <IconButton
                                        edge="end"
                                        color="success"
                                        onClick={handleSaveEditTerm}
                                        disabled={editingTerm?.saving}
                                      >
                                        {editingTerm?.saving ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <CheckIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title="Editează termenul">
                                    <span>
                                      <IconButton
                                        edge="end"
                                        color="primary"
                                        onClick={() =>
                                          handleStartEditTerm(
                                            attribute.id,
                                            term
                                          )
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Șterge termenul">
                                    <span>
                                      <IconButton
                                        edge="end"
                                        color="error"
                                        disabled={
                                          termState.deleting === currentTermId
                                        }
                                        onClick={() =>
                                          handleDeleteTerm(
                                            attribute.id,
                                            currentTermId
                                          )
                                        }
                                      >
                                        {termState.deleting ===
                                        currentTermId ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <DeleteOutlineIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Adaugă termen nou
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Nume"
                  required
                  fullWidth
                  value={formValues.name}
                  onChange={(e) =>
                    handleFormChange(attribute.id, "name", e.target.value)
                  }
                />
                <TextField
                  label="Slug (opțional)"
                  fullWidth
                  value={formValues.slug}
                  onChange={(e) =>
                    handleFormChange(attribute.id, "slug", e.target.value)
                  }
                />
                <TextField
                  label="Descriere (opțional)"
                  fullWidth
                  value={formValues.description}
                  onChange={(e) =>
                    handleFormChange(
                      attribute.id,
                      "description",
                      e.target.value
                    )
                  }
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={termState.saving}
                  onClick={() => handleAddTerm(attribute.id)}
                >
                  {termState.saving ? "Se salvează…" : "Adaugă"}
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Accordion
        sx={{
          mb: 2,
          borderRadius: 4,
          overflow: "hidden",
          background: "rgba(255,255,255,0.95)",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            width="100%"
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Tags WooCommerce
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vizualizează rapid tag-urile existente în magazinul WooCommerce.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`${wooTags.length} taguri`}
                color="primary"
                variant="outlined"
              />
              <Tooltip title="Reîncarcă tag-urile">
                <span>
                  <IconButton onClick={fetchWooTags} disabled={tagsLoading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 4 }}>
          {tagsLoading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
              <Typography mt={2} color="text.secondary">
                Încărcăm tag-urile din WooCommerce…
              </Typography>
            </Box>
          ) : tagsError ? (
            <Alert severity="error">{tagsError}</Alert>
          ) : wooTags.length === 0 ? (
            <Alert severity="info">
              Nu am găsit tag-uri în WooCommerce momentan.
            </Alert>
          ) : (
            <>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Tag-uri ({filteredTags.length})
                </Typography>
                <TextField
                  label="Caută tag"
                  size="small"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  placeholder="ex: Hermes, Luxury, etc."
                  sx={{ maxWidth: 260 }}
                />
              </Stack>
              <List dense disablePadding>
                {filteredTags.map((tag) => (
                  <ListItem
                    key={tag.id}
                    divider
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      bgcolor: "rgba(15,23,42,0.02)",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography fontWeight={600}>{tag.name}</Typography>
                          {tag.slug && (
                            <Chip
                              label={tag.slug}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        tag.description
                          ? tag.description
                          : "Fără descriere specificată."
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={`${tag.count || 0} produse`}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SEOAttributesPage;
